import { clsx } from 'clsx'
import { Space } from 'component/Space'
import { Typo } from 'component/Typo'
import { Vertical } from 'component/Vertical'
import { ym } from 'feature/yama'
import { useBodyScrollLock } from 'hook/useBodyScrollLock'
import { useEffectOnce } from 'hook/useEffectOnce'
import { useLatestCallback } from 'hook/useLatestCallback'
import { i18n } from 'i18n'
import { observer } from 'mobx-react-lite'
import LogoSvg from 'page/catalog/BasicLogo/salaris-dresscode.svg'
import AuroraSvg from 'page/catalog/BasicCarouselWelcome/aurora.svg'
import { CarouselItemsStore } from 'page/catalog/BasicCarouselWelcome/CarouselItemsStore'
import { Paginator } from 'page/catalog/BasicCarouselWelcome/Paginator'
import { Slides } from 'page/catalog/BasicCarouselWelcome/Slides'
import { Doc } from 'page/catalog/BasicWelcomePage/Doc'
import { TimeoutModal } from 'page/catalog/BasicWelcomePage/TimeoutModal/TimeoutModal'
import { useDemo } from 'page/catalog/store/CatalogContext'
import { itemPreview, ItemPreviewStore } from 'page/catalog/store/ItemPreviewStore'
import { SyntheticEvent, useMemo } from 'react'
import { space } from 'util/typo'
import css from './BasicCarouselWelcome.module.scss'

export const BasicCarouselWelcome = observer(() => {
  const demo = useDemo()
  const welcome = demo.welcome!
  const store = useMemo(() => new CarouselItemsStore(demo.product, welcome.carouselLimit), [demo, welcome])
  const { items, index } = store
  const item = store.item
  const { brands } = demo.product.details ?? {}

  useBodyScrollLock(true)

  useEffectOnce(() => {
    void store.load()
  })

  const onSlideChange = useLatestCallback((index: number) => {
    store.index = index
  })

  const onTry = useLatestCallback(() => {
    const item = store.item
    if (!item) return
    ym('click_try_on', { from: 'basic_welcome', item_id: item.item_id })
    demo.startBasic()
    itemPreview.open(new ItemPreviewStore(item, true))
  })

  const onCatalog = useLatestCallback(() => {
    demo.startBasic()
  })

  const onClickPolicy = useLatestCallback((event: SyntheticEvent) => {
    event.preventDefault()
    welcome.doc = welcome.policy
  })

  const onClickData = useLatestCallback((event: SyntheticEvent) => {
    event.preventDefault()
    welcome.doc = welcome.data
  })

  return <Vertical className={css.page} center>
    <AuroraSvg className={css.aurora} />

    <Space className={css.head} center>
      <Space className={css.logo}>
        <LogoSvg />
      </Space>
    </Space>

    <Space className={css.carousel} wide spring>
      {!!items.length && <Slides items={items} delay={welcome.carouselDelay} onChange={onSlideChange} onTry={onTry} />}
    </Space>

    <Space className={css.dots} center>
      <Paginator count={items.length} index={index} />
    </Space>

    <Vertical className={css.details} gap={8} center>
      <Typo className={css.name} size={32} medium upper center>
        {item?.product_name}
      </Typo>
      {brands && !!item?.brand_name && <Space gap={8} center>
        <Typo className={css.label} size={20} upper one>
          {i18n('demo.Brand')}:
        </Typo>
        <Typo className={css.value} size={20} upper one ellipsis>
          {item.brand_name}
        </Typo>
      </Space>}
    </Vertical>

    <Vertical className={css.buttons} gap={24}>
      <Space className={clsx(css.button, css.primary)} pad={16} round={16} center onClick={onTry}>
        <Typo size={24} semi upper one center>
          {i18n('TryOnThisLook')}
        </Typo>
      </Space>
      <Space className={clsx(css.button, css.secondary)} pad={16} round={16} center onClick={onCatalog}>
        <Typo size={24} semi upper one center>
          {i18n('GoToCatalog')}
        </Typo>
      </Space>

      <Space className={css.consent} center>
        <Typo size={16} center>
          Нажимая кнопку «Примерить» вы подтверждаете, что ознакомились с
          {space}
          <Space className={css.underline} onClick={onClickPolicy}>
            <Typo>политикой конфиденциальности</Typo>
          </Space>
          {space}
          и даете
          {space}
          <Space className={css.underline} onClick={onClickData}>
            <Typo>согласие на обработку персональных данных</Typo>
          </Space>
        </Typo>
      </Space>
    </Vertical>

    {welcome.doc && <Doc />}
    {!welcome.timeout.active && <TimeoutModal />}
  </Vertical>
})
